import { useEffect, useMemo, useState } from "react";
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { banksColumns, columnLabelsMap } from "@/modules/banks/TableColumnsDef.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { DataTable } from "@/components/table/DataTable.tsx";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { getVisibilityStateValue, setVisibilityStateValue } from "@/lib/table.ts";

export const Route = createFileRoute("/settings/banks/")({
  component: RouteComponent,
});

const BANKS_COLUMN_VISIBILITY_KEY = "@fin_merch_banks_visible_columns";

function RouteComponent() {
  const title = t`Bank administration`;

  const { sortedUnstructuredData, codesOptions, countryOptions } = useBankData();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getVisibilityStateValue(BANKS_COLUMN_VISIBILITY_KEY),
  );

  useEffect(() => {
    setVisibilityStateValue(BANKS_COLUMN_VISIBILITY_KEY, columnVisibility);
  }, [columnVisibility]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const columns = useMemo(() => banksColumns(codesOptions, countryOptions), [codesOptions, countryOptions]);

  const table = useReactTable({
    columns,
    data: sortedUnstructuredData ?? [],
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  const labels = useMemo(() => {
    return columnLabelsMap();
  }, []);

  return (
    <PageWrapper>
      <PageHeader breadcrumbs={[{ href: "/", title }]} />
      <div className="flex flex-col grow p-5">
        <DataTable table={table} labels={labels} showPagination={false} />
      </div>
    </PageWrapper>
  );
}
